/**
 * Números do cartão do município (§8.4), funções puras. Duas fontes, por ordem:
 *
 * 1. `StateDetail` da UF (§14.4): seções, eleitorado, comparecimento, brancos, nulos e os votos do par, pelo código do
 *    município em `keys`. É a fonte completa do cartão.
 * 2. As colunas municipais da vista (`ResultsView.municipal`, `[fração do eleitorado, fatia de B, margem, vantagem]` por
 *    município): enquanto o detalhe não chega, só as duas fatias (§8.4 "Dados", último item).
 *
 * Par sempre na ordem do contrato `[A, B]` = [13, 22]: o presidente nunca é ordenado por votos (regra inviolável 2;
 * `docs/DECISOES.md`, 10/10, a mesma regra do `UfCard`). Sem abstenção: a tela nunca a calcula por subtração
 * (`docs/DECISOES.md`, item 14, e §28), e o `StateDetail` não traz a coluna oficial.
 */
import type { StateDetail } from '../contracts/municipal';
import type { Candidate } from './types';

export interface MunicipalityNumbers {
  sections: number;
  sectionsCounted: number;
  /** Votos oficiais de [A, B]. */
  votes: [number, number];
  /** A + B. */
  valid: number;
  /** Fatias dos válidos; null sem voto válido. */
  shares: [number, number] | null;
  /** `turnout ÷ electorate_counted`; null sem eleitorado apurado. */
  turnoutRate: number | null;
  /** `(blank + null) ÷ turnout`; null sem comparecimento. */
  blankNullRate: number | null;
}

/** A linha do município no detalhe da UF; null se o detalhe não traz o município ou um dos dois números do par. */
export function municipalityFromDetail(
  detail: StateDetail | null | undefined,
  ibge: string,
  pair: readonly [Pick<Candidate, 'number'>, Pick<Candidate, 'number'>],
): MunicipalityNumbers | null {
  if (!detail) return null;
  const i = detail.keys.indexOf(ibge);
  if (i < 0) return null;
  const c = detail.columns;
  const a = c.votes[pair[0].number]?.[i];
  const b = c.votes[pair[1].number]?.[i];
  const sections = c.sections[i];
  const counted = c.sections_counted[i];
  if (a === undefined || b === undefined || sections === undefined || counted === undefined) return null;
  const electorateCounted = c.electorate_counted[i] ?? 0;
  const turnout = c.turnout[i] ?? 0;
  const blankNull = (c.blank_votes[i] ?? 0) + (c.null_votes[i] ?? 0);
  const valid = a + b;
  const turnoutRate = electorateCounted > 0 ? Math.min(1, turnout / electorateCounted) : null;
  return {
    sections,
    sectionsCounted: counted,
    votes: [a, b],
    valid,
    shares: valid > 0 ? [a / valid, b / valid] : null,
    turnoutRate,
    blankNullRate: turnout > 0 ? blankNull / turnout : null,
  };
}

/** Fração do eleitorado apurado e fatias do município nas colunas da vista; null sem dado (mesma regra do tooltip). */
export function municipalityFromColumns(
  cols: Float32Array | null | undefined,
  idx: number,
): { fraction: number; shares: [number, number] | null } | null {
  const fraction = cols?.[idx * 4];
  const shareB = cols?.[idx * 4 + 1];
  const adv = cols?.[idx * 4 + 3];
  if (fraction === undefined || shareB === undefined || adv === undefined || !(fraction >= 0)) return null;
  // sem voto válido o decodeBlock grava fatia de B = 0 e vantagem 0 (§14.4; map/paint.ts e features/map/tooltip.ts)
  const noValid = adv === 0 && shareB !== 0.5;
  return { fraction, shares: fraction > 0 && !noValid && Number.isFinite(shareB) ? [1 - shareB, shareB] : null };
}

/** Chip da seção Presidente (§8.4): "Apurado" com todas as seções, "Aguardando" sem nenhuma, nada enquanto apura. */
export type MunicipalityChip = 'counted' | 'waiting' | null;

export function municipalityChip(
  n: Pick<MunicipalityNumbers, 'sections' | 'sectionsCounted'> | null,
): MunicipalityChip {
  if (!n || n.sectionsCounted <= 0) return 'waiting';
  return n.sections > 0 && n.sectionsCounted >= n.sections ? 'counted' : null;
}
